import { Injectable, Injector } from '@angular/core';
import {
  AbstractControlOptions,
  FormArray,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { UserDao } from '@app/dao/user/user.dao';
import { PledgeProductRead } from '@app/forms/subscription/definition/interface/Pledge';
import { prestationJointOwnerships } from '@app/forms/subscription/definition/interface/subscription';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import { FormValidators } from '@app/providers/utils/form-validators';
import * as _ from 'lodash';
import FormGroupArray from '../../../services/FormGroupArray';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  userId;
  sessionConsultant: boolean;
  constructor(
    protected formConstructor: PrFormConstructor,
    protected injector: Injector,
  ) {
    super(formConstructor);
    injector
      .get(UserDao)
      .authOnce$()
      .subscribe(user => {
        this.userId = user.id;
      });
  }

  generateForm(
    entity: PledgeProductRead & { parameters: any },
    option,
  ): FormGroup {
    let options: AbstractControlOptions = {};
    this.sessionConsultant = option.sessionConsultant;

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        prestationJointOwnershipList: this.formBuilder.array([
          this.formGroup(),
        ]),
      }),
    };

    const formBuilderGroup = this.formBuilder.group(formConfig, options);

    if (
      entity &&
      entity.parameters &&
      Array.isArray(entity.parameters.prestationJointOwnershipList) &&
      entity.parameters.prestationJointOwnershipList.length > 0
    ) {
      const prestationArray = this.populate(entity.parameters);
      const parametersGroup = formBuilderGroup.get('parameters') as FormGroup;
      parametersGroup.setControl(
        'prestationJointOwnershipList',
        prestationArray,
      );
    }

    return formBuilderGroup;
  }

  generate(entity: any[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }
  get products() {
    return this.form.get('products') as FormArray;
  }
  populate(data: prestationJointOwnerships) {
    let formArray = this.formBuilder.array([this.formGroup()]);
    if (data) {
      formArray.clear();
      data.prestationJointOwnershipList.forEach((item, index) => {
        formArray.push(
          this.formBuilder.group({
            lastname: [item.lastname, Validators.required],
            firstname: [item.firstname, Validators.required],
            birthdate: [
              item?.birthdate || new Date('01/01/1970'),
              Validators.required,
            ],
            birthLocation: [item?.birthLocation || '', Validators.required],
            birthZipCode: [item?.birthZipCode || '', Validators.required],
            nationality: [item?.nationality || '', Validators.required],
            socialSecurityNumber: [
              item?.socialSecurityNumber,
              [
                !this.sessionConsultant ? Validators.required : null,
                FormValidators.socialSecurityNumber,
              ].filter(validator => validator !== null),
            ],
            enrollmentOffice: [
              item?.enrollmentOffice,
              !this.sessionConsultant ? Validators.required : null,
            ],
            postalAddress: [item?.postalAddress, Validators.required],
            email: [item?.email, Validators.required],
            phone: [item?.phone, Validators.required],
            maritalSituation: [item?.maritalSituation, Validators.required],
            fiscalNumber: [
              item?.fiscalNumber,
              [
                !this.sessionConsultant ? Validators.required : null,
                Validators.minLength(9),
                Validators.maxLength(13),
              ].filter(validator => validator !== null),
            ],
          }),
        );
      });
    }

    return formArray;
  }
  formGroup(): FormGroup {
    return this.formBuilder.group({
      lastname: ['', Validators.required],
      firstname: ['', Validators.required],
      birthdate: [new Date('01/01/1970'), Validators.required],
      birthLocation: ['', Validators.required],
      birthZipCode: ['', Validators.required],
      nationality: ['', Validators.required],
      socialSecurityNumber: [
        '',
        [
          !this.sessionConsultant ? Validators.required : null,
          FormValidators.socialSecurityNumber,
        ].filter(validator => validator !== null),
      ],
      enrollmentOffice: [
        '',
        !this.sessionConsultant ? Validators.required : null,
      ],
      postalAddress: ['', Validators.required],
      email: ['', Validators.required],
      phone: ['', Validators.required],
      maritalSituation: ['', Validators.required],
      fiscalNumber: [
        '',
        [
          !this.sessionConsultant ? Validators.required : null,
          Validators.minLength(9),
          Validators.maxLength(13),
        ].filter(validator => validator !== null),
      ],
    });
  }
  extract() {
    return _.cloneDeep(this.form?.value?.products);
  }
}
